// Thin selector wrappers, all of them pure functions over GameState.
// React hooks live in ./hooks.ts so this file stays runtime-agnostic.

import {
  balanceSheet,
  cashflowForWeek,
  cashflowThisWeek,
  livingExpensesForWeek,
  passiveIncomeForWeek,
  plForRange,
  winProgress,
  type BalanceSheet,
  type GameState,
  type ProfitAndLoss,
} from '@biz-kid/engine';

export const selectCash = (s: GameState): number => s.wallet.cash;
export const selectWeek = (s: GameState): number => s.clock.week;
export const selectDayOfWeek = (s: GameState): number => s.clock.dayOfWeek;
export const selectAP = (s: GameState): { current: number; perDay: number } => s.ap;
export const selectLevel = (s: GameState): number => s.profile.level;
export const selectStreak = (s: GameState): number => s.profile.streak.current;
export const selectLongestStreak = (s: GameState): number => s.profile.streak.longest;
export const selectLastWakeUpDayKey = (s: GameState): string | undefined =>
  s.profile.streak.lastWakeUpDayKey;
export const selectXP = (s: GameState): number => s.profile.xp;
export const selectReputation = (s: GameState): number => s.profile.reputation;
export const selectHappiness = (s: GameState): number => s.profile.happiness;
export const selectLastDecisionWeek = (s: GameState): number => s.lastDecisionWeek ?? 0;

export const selectCashflowThisWeek = (s: GameState): number => cashflowThisWeek(s);
export const selectCashflowForWeek = (week: number) => (s: GameState): number =>
  cashflowForWeek(s, week);
export const selectPassiveIncome = (s: GameState): number => passiveIncomeForWeek(s);
export const selectLivingExpenses = (s: GameState): number => livingExpensesForWeek(s);
export const selectWinProgress = (s: GameState): number => winProgress(s);
export const selectBalanceSheet = (s: GameState): BalanceSheet => balanceSheet(s);
export const selectPLForRange =
  (from: number, to: number) =>
  (s: GameState): ProfitAndLoss =>
    plForRange(s, from, to);

export const selectBusinesses = (s: GameState) => s.businesses;
export const selectAssets = (s: GameState) => s.assets;
export const selectLiabilities = (s: GameState) => s.liabilities;
export const selectLoans = (s: GameState) => s.loans;
export const selectSavings = (s: GameState) => s.savings;
export const selectServices = (s: GameState) => s.services;
export const selectSocial = (s: GameState) => s.social;
export const selectGaming = (s: GameState) => s.gaming;
export const selectTrade = (s: GameState) => s.trade;
export const selectProperty = (s: GameState) => s.property;
export const selectBroker = (s: GameState) => s.broker;
export const selectInvestors = (s: GameState) => s.investors ?? [];
export const selectInvestorShare = (s: GameState): number =>
  (s.investors ?? []).reduce((sum, inv) => sum + inv.sharePct, 0);
export const selectLedger = (s: GameState) => s.ledger;
